import React, { useEffect, useState } from "react";
import { State, City } from "country-state-city";
import { singleteam } from "redux-store/care-team/care-team.selector";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "redux-store/store";
import { addNewteam, addNewteamByAdmin, getSingleteam, updateSingleteam } from "redux-store/care-team/care-team.slice";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { teamActivityInProcess, successMessage, teamError } from "redux-store/care-team/care-team.selector";
import { errorClean, messageClean } from "redux-store/care-team/care-team.slice";
import * as Yup from "yup";
import { ValidationError } from "yup";

interface FormData {
  userId: string | undefined;
  _id: string | undefined;
  hospitalName: string;
  email: string;
  veterinarianName: string;
  phone: string;
  zipCode: string;
  apartment: string;
  state: string;
  city: string;
  address: string;
}

interface ErrorMessages {
  [key: string]: string;
}

const initialFormData: FormData = {
  userId: "",
  _id: "",
  hospitalName: "",
  email: "",
  veterinarianName: "",
  phone: "",
  zipCode: "",
  apartment: "",
  state: "",
  city: "",
  address: "",
};

const initialErrorMessages: ErrorMessages = {
  hospitalName: "",
  veterinarianName: "",
  email: "",
  phone: "",
  zipCode: "",
  address: "",
  city: "",
  state: "",
};

export default function AddVet() {
  const [showFullText, setShowFullText] = useState(false);
  // const [checked, setChecked] = useState(false);
  const [states, setStates] = useState<any | null>([]);
  const [cities, setCities] = useState<any | null>([]);

  const [countryCode, setCountryCode] = useState("US");
  const [stateCode, setStateCode] = useState("");
  const team = useSelector(singleteam);
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(teamActivityInProcess);
  const teamMessage = useSelector(successMessage);
  const errorMessage = useSelector(teamError);
  const userId = params?.userId;
  const [searchParams] = useSearchParams();
  const edit = searchParams.get("edit");
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  const userFormValidationSchema = Yup.object().shape({
    hospitalName: Yup.string().required("Hospital Name is required"),
    veterinarianName: Yup.string().required("Veterinarian Name is required"),
    email: Yup.string().email("Email Not Valid").required("Email is required"),
    phone: Yup.string()
      .required("Phone Number is required")
      .max(11, "Phone number cannot be longer than 11 characters"),
    zipCode: Yup.string().required("Zip Code is required"),
    address: Yup.string().required("Address is required"),
    apartment: Yup.string(),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
  });

  const [errMessages, setErrMessages] = useState<ErrorMessages>(initialErrorMessages);

  const data = [
    {
      name: "hospitalName",
      label: "Veterinary Hospital Name",
      type: "text",
      placeholder: "Enter Veterinary Hospital Name",
      required: true,
    },
    {
      name: "veterinarianName",
      label: "Veterinarian Name",
      type: "text",
      placeholder: "Enter Veterinarian Name",
      required: true,
    },
    {
      name: "email",
      label: "Email",
      type: "email",
      placeholder: "Enter Veterinarian / Hospital Email",
      required: true,
    },
    {
      name: "phone",
      label: "Phone Number",
      type: "number",
      placeholder: "Enter Veterinarian / Hospital Phone Number",
      required: true,
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      placeholder: "Enter Veterinarian / Hospital Address",
      required: true,
    },
    {
      name: "apartment",
      label: "Apartment, Suite, Building",
      type: "text",
      placeholder: "Enter Address Line 2",
      required: false,
    },
    { name: "city", label: "City", type: "text", placeholder: "Enter City", required: true },
    {
      name: "zipCode",
      label: "Zip Code",
      type: "number",
      placeholder: "Enter Zip Code",
      required: true,
    },
  ];
  useEffect(() => {
    setFormData({ ...formData, userId: userId });
  }, [userId]);

  const cityData = (stateCode: string) => {
    let newCities = City.getCitiesOfState(countryCode, stateCode).map((city) => ({
      name: city.name,
    }));
    setCities(newCities);
  };
  const stateData = () => {
    let newStates = State.getStatesOfCountry(countryCode).map((state) => ({
      name: state.name,
      code: state.isoCode,
    }));
    setStates(newStates);
  };

  const onChange = (e: any) => {
    const value = e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const stateChangeHandler = (name: any) => {
    setFormData({ ...formData, state: name });
  };

  const handleAddClick = async () => {
    try {
      await userFormValidationSchema.validate(formData, { abortEarly: false });
      if (!edit && userId) {
        dispatch(addNewteamByAdmin(formData));
      }
      if (edit && userId) {
        dispatch(updateSingleteam(formData));
        alert("Updated Successfully");
        navigate("/admin/users");
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        const newErrorMessages: ErrorMessages = {};
        error.inner.forEach((err) => {
          if (err.path) {
            newErrorMessages[err.path] = err.message;
          }
        });
        setErrMessages(newErrorMessages);
      } else {
        console.error("Unexpected validation error:", error);
      }
    }
  };

  useEffect(() => {
    dispatch(errorClean());
    dispatch(messageClean());
    stateData();
  }, []);

  useEffect(() => {
    if (userId) {
      dispatch(getSingleteam(userId));
    }
  }, [userId]);

  useEffect(() => {
    dispatch(errorClean());
    dispatch(messageClean());

    if (teamMessage !== "") {
      setErrMessages({ ...errMessages, emptyMessage: "" });
      dispatch(errorClean());
      dispatch(messageClean());
      navigate(`/admin/users`);
    }
  }, [teamMessage]);

  useEffect(() => {
    if (stateCode) {
      cityData(stateCode);
    }
  }, [stateCode]);

  useEffect(() => {
    if (team) {
      setFormData({
        userId: userId || "",
        _id: team._id || "",
        hospitalName: team.hospitalName || "",
        email: team.email || "",
        veterinarianName: team.veterinarianName || "",
        phone: team.phone || "",
        zipCode: team.zipCode || "",
        apartment: team.apartment || "",
        state: team.state || "",
        city: team.city || "",
        address: team.address || "",
      });
      setIsLoading(false);
    }
  }, [team, userId]);

  const toggleFullText = () => {
    setShowFullText((prev) => !prev);
  };

  const text =
    "Although providing your veterinarian's information is optional, we strongly encourage it. Upholding the veterinary-client-patient relationship (VCPR) is important to us, and we advise sharing your pet's test results with one or more veterinarians for expert medical insights and guidance, regardless of whether the results are positive or negative. If you have a preferred veterinarian, we will automatically share the results with them to ensure these findings are incorporated into your pets medical records. In the event of a positive test result, your veterinarian can also help with treatment and care. If you don't have a veterinarian, feel free to consult with our in-house expert or allow us to connect you with our virtual veterinarian partners.";

  const truncatedText = showFullText ? text : text.slice(0, text.length / 5);

  return (
    <div className="tw-relative tw-top-[-9.375rem] tw-px-3 md:tw-px-5 lg:tw-px-10 tw-flex tw-flex-col tw-gap-10">
      <div className="tw-px-6 sm:tw-px-8 md:tw-px-8 lg:tw-px-10 tw-py-2 tw-flex tw-flex-col gap-3 tw-w-full tw-bg-[#FFFFFF] tw-rounded-2xl tw-shadow-3xl">
        <div>
          <span className="tw-font-bold tw-text-[24px]  md:tw-text-[26px] lg:tw-text-[32px]">Contact Details</span>
        </div>
        <div className="tw-flex tw-gap-5 tw-text-[20px]">
          <span className="tw-text-[16px] tw-font-light">
            {truncatedText}
            {!showFullText && (
              <p className="tw-text-reddishOrange tw-cursor-pointer" onClick={toggleFullText}>
                Learn More
              </p>
            )}
            {showFullText && (
              <p className="tw-text-reddishOrange tw-cursor-pointer" onClick={toggleFullText}>
                Fold Back
              </p>
            )}
          </span>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-4 tw-text-[16px] lg:tw-text-[20px]">
          {data.map((d, i) =>
            !(d.label === "City") ? (
              <div className="tw-flex tw-flex-col tw-flex-1 tw-items-start" key={i}>
                <label className="tw-font-semibold">
                  {d.label}
                  {d.required ? "*" : ""}
                </label>
                <input
                  type={d.type}
                  name={d.name}
                  className="tw-border tw-border-gray-400 tw-text-black tw-rounded-lg tw-text-sm tw-p-4 tw-my-2 tw-w-full md:tw-w-10/12 lg:tw-w-1/2 tw-placeholder-gray-400 tw-font-light"
                  placeholder={d.placeholder}
                  onChange={onChange}
                  value={`${(formData as any)[d?.name] ? (formData as any)[d?.name] : ""}`}
                />
                <span className=" tw-text-xs tw-text-red-500">
                  {d.name === "hospitalName"
                    ? errMessages.hospitalName
                    : d.name === "veterinarianName"
                    ? errMessages.veterinarianName
                    : d.name === "phone"
                    ? errMessages.phone
                    : d.name === "address"
                    ? errMessages.address
                    : d.name === "email"
                    ? errMessages.email
                    : d.name === "zipCode"
                    ? errMessages.zipCode
                    : null}
                </span>
              </div>
            ) : (
              <div className="tw-flex tw-flex-col lg:tw-flex-row tw-w-full md:tw-w-10/12 lg:tw-w-1/2 tw-gap-6" key={i}>
                <div className="tw-flex tw-flex-col tw-items-start tw-flex-1">
                  <label className="tw-font-semibold">State*</label>
                  <select
                    className="tw-border tw-border-gray-400 tw-text-black tw-rounded-lg tw-text-sm tw-p-4 tw-my-2 tw-w-full tw-placeholder-gray-400 tw-font-light"
                    name="state"
                    onChange={(e) => {
                      setStateCode(e.target.value);
                      stateChangeHandler(e.target.children[e.target.selectedIndex].innerHTML);
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((option: any, index: any) => {
                      return (
                        <option key={index} value={option.code}>
                          {option.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className=" tw-text-xs tw-text-red-500">
                    {errMessages.state !== "" ? errMessages.state : ""}
                  </span>
                </div>

                <div className="tw-flex tw-flex-col tw-items-start tw-flex-1">
                  <label className="tw-font-semibold">City*</label>
                  <select
                    className="tw-border tw-border-gray-400 tw-text-black tw-rounded-lg tw-text-sm tw-p-4 tw-my-2 tw-w-full tw-placeholder-gray-400 tw-font-light"
                    name="city"
                    onChange={(e) => {
                      onChange(e);
                    }}
                    value={`${(formData as any)["city"] ? (formData as any)["city"] : ""}`}
                  >
                    <option value="">Select City</option>
                    {cities.map((option: any, index: any) => {
                      return (
                        <option key={index} value={option.name}>
                          {option.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className=" tw-text-xs tw-text-red-500">{errMessages.city !== "" ? errMessages.city : ""}</span>
                </div>
              </div>
            )
          )}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className=" tw-text-xs tw-text-red-500">{errMessages.emptyMessage}</span>
          <span className=" tw-text-xs tw-text-red-500">{errorMessage.message ? errorMessage.message : ""}</span>
          <div className="tw-flex tw-gap-8 tw-text-[16px] sm:tw-text-[20px] tw-mb-8 tw-mt-6 tw-place-items-center">
            <button
              disabled={loading}
              className="tw-flex tw-items-center tw-p-1 sm:tw-p-3 tw-gap-2 tw-justify-center tw-w-[100px] sm:tw-w-[116px] tw-h-[42px] sm:tw-h-[48px] tw-left-[160px] tw-top-[783.48px] tw-bg-[#FF503D] tw-rounded-xl tw-text-white"
              onClick={() => {
                handleAddClick();
              }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              className="tw-flex tw-border tw-border-black tw-p-1 sm:tw-p-3 tw-justify-center tw-items-center tw-w-[100px] sm:tw-w-[116px] tw-h-[42px] sm:tw-h-[48px] tw-rounded-xl"
              onClick={() => {
                navigate(`/admin/users`);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
